import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { FormsModule } from '@angular/forms';
import { Browser } from '@capacitor/browser';
import { AlertController, IonIcon, IonSkeletonText } from "@ionic/angular/standalone";
import { Store } from "@ngrx/store";
import { ValidationUtil } from "@qid/core";
import { SexyModule } from '@qid/sexy';
import { AvatarRoundComponent, SuperButtonComponent, SuperInputComponent } from '@qid/super';
import { filter, firstValueFrom, take } from "rxjs";
import { DisplayNameComponent } from "../../../../components/display-name/display-name.component";
import { AccountProfileFacade } from "../../../account-profile";
import { OtpService, UsersActions, UsersSelectors } from "../../../user";
import { CreateUserDto } from "../../../user/lib/dto/create-user.dto";
import { OtpChannel } from "../../../user/lib/enum/OtpChannel";
import { OtpVerifier } from "../../../user/lib/model/OtpVerifier";
import { User } from "../../../user/lib/model/User";
import { UserService } from "../../../user/lib/services/user.service";
import { AuthenticationService } from "../../index";

@Component({
  selector: "quick-signup",
  templateUrl: "./quick-signup.component.html",
  styleUrls: ["./quick-signup.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SexyModule,
    SuperButtonComponent,
    SuperInputComponent,
    AvatarRoundComponent,
    DisplayNameComponent,
    IonIcon,
    IonSkeletonText
  ],
})
export class QuickSignupComponent implements OnInit, OnChanges {

  @Input() defaultEmail: string = null
  @Input() defaultName: string = null
  @Input() defaultPhone: string = null
  /** When true, sign-in email/phone are pre-filled and readonly; channel switches hidden when only one channel. */
  @Input() lockContacts = false

  @Input() isOtpVerification: boolean;
  @Input() isEmailRequired: boolean;
  @Input() isPhoneRequired: boolean;

  /**
   * When set, if the phone/email is not registered yet, show a "complete setup" slide
   * instead of the in-app sign-up flow, with a button that opens this URL (e.g. Business Suite `/starter`).
   */
  @Input() completeSetupInBusinessSuiteUrl: string | null = null;

  /** When true, after opening the Business Suite URL (Continue), call `window.close()` (e.g. Chrome extension popup). */
  @Input() closeWindowAfterSuiteContinue = false;

  @Output() onLogin: EventEmitter<User> = new EventEmitter<User>();

  public createUserDto: CreateUserDto = new CreateUserDto();
  public authenticatedUser$ = this.store.select(UsersSelectors.selectAuthenticatedUser);
  public accountProfile$ = this.accountProfileFacade.profile$

  public slides = {
    loggedIn: "loggedIn",
    signIn: "signIn",
    signUp: "signUp",
    requiredContact: "requiredContact",
    loading: "loading",
    defaultCredential: "defaultCredential",
    accountFound: "accountFound",
    setupIncomplete: "setupIncomplete",
  };
  public activeSlide: any = this.slides.loading;
  public isLoginWithEmail: boolean;
  public defaultCredentialUser: User;
  public isSubmittingSignIn = false;

  public existingAccount: any = null
  public existingAccountFoundAgainst: 'phone' | 'email' | null = null
  public lockedFlowContactLabel: string = null;

  constructor(
    private userService: UserService,
    private store: Store,
    private authenticationService: AuthenticationService,
    private otpService: OtpService,
    private alertController: AlertController,
    private accountProfileFacade: AccountProfileFacade
  ) {
    this.createUserDto.phoneVerifier = new OtpVerifier(OtpChannel.phone);
    this.createUserDto.emailVerifier = new OtpVerifier(OtpChannel.email);
  }

  ngOnInit() {
    this.applyLockedContactToVerifiers();
    this.isLoginWithEmail =
      !!this.defaultEmail || (!this.defaultPhone && !!this.isEmailRequired);
    this.checkLoginStatus();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      !changes['defaultEmail'] &&
      !changes['defaultPhone'] &&
      !changes['defaultName'] &&
      !changes['lockContacts']
    ) {
      return;
    }
    this.applyLockedContactToVerifiers();
    this.isLoginWithEmail =
      !!this.defaultEmail || (!this.defaultPhone && !!this.isEmailRequired);
  }

  /** Re-apply locked email/phone after verifier reset (e.g. channel switch). */
  private applyLockedContactToVerifiers(): void {
    if (this.defaultName) {
      this.createUserDto.name = this.defaultName;
    }
    if (this.defaultEmail) {
      this.createUserDto.emailVerifier.receiverId = this.defaultEmail;
    }
    if (this.defaultPhone) {
      this.createUserDto.phoneVerifier.receiverId = this.normalizeLockedPhone(this.defaultPhone);
    }
  }

  get showSwitchToEmail(): boolean {
    return !this.isLoginWithEmail && (!this.lockContacts || !!this.defaultEmail);
  }

  get showSwitchToPhone(): boolean {
    return this.isLoginWithEmail && (!this.lockContacts || !!this.defaultPhone);
  }

  get showAuthChannelSwitchRow(): boolean {
    return this.showSwitchToEmail || this.showSwitchToPhone;
  }

  get canShowLogoutOption(): boolean {
    const hasAdminParticipantDefaults =
      !!this.defaultName || !!this.defaultEmail || !!this.defaultPhone;
    return !this.lockContacts && !hasAdminParticipantDefaults;
  }

  checkLoginStatus() {
    this.authenticationService.checkPreviousAuthenticatedUser().then(
      token => {
        this.onLoginStatusChecked(token)
      }
    )
  }

  async onLoginStatusChecked(token: string) {
    const hasLocked =
      !!this.lockContacts && (!!this.defaultEmail || !!this.defaultPhone);
    const legacyEmailShortcut =
      ValidationUtil.isEmailValid(this.defaultEmail) && !this.lockContacts;

    if (token && hasLocked) {
      const user = await this.resolveAuthenticatedUser();
      if (!user) {
        this.applyLockedContactToVerifiers();
        return this.goToSlide(this.slides.signIn);
      }
      let mismatch = false;
      if (this.defaultEmail && user.email !== this.defaultEmail) {
        mismatch = true;
      }
      if (
        this.defaultPhone &&
        this.normalizeComparablePhone(user.phoneNumber) !==
        this.normalizeComparablePhone(this.defaultPhone)
      ) {
        mismatch = true;
      }
      if (mismatch) {
        await this.authenticationService.signOut();
        this.applyLockedContactToVerifiers();
        this.isLoginWithEmail =
          !!this.defaultEmail || (!this.defaultPhone && !!this.isEmailRequired);
        if (this.isLockedCollectionFlow()) {
          return this.prepareLockedFlowEntry();
        }
        return this.goToSlide(this.slides.signIn);
      }
      return this.routePostAuth();
    }

    if (!token && !legacyEmailShortcut) {
      if (this.isLockedCollectionFlow()) {
        return this.prepareLockedFlowEntry();
      }
      return this.goToSlide(this.slides.signIn);
    }

    if (!token && legacyEmailShortcut) {
      return this.loginFlowForDefaultCredential();
    }

    if (token && !legacyEmailShortcut) {
      return this.routePostAuth();
    }

    if (token && legacyEmailShortcut) {
      this.authenticatedUser$.subscribe((user) => {
        if (!user) {
          return;
        }
        if (user.email == this.defaultEmail) {
          return this.routePostAuth();
        }
        this.loginFlowForDefaultCredential();
      });
    }
  }

  async loginFlowForDefaultCredential() {

    this.createUserDto.emailVerifier.receiverId = this.defaultEmail

    const checkEmailUser = await this.userService.checkEmail(this.defaultEmail);

    if (checkEmailUser) {
      this.defaultCredentialUser = checkEmailUser
      this.goToSlide(this.slides.defaultCredential);
      return
    }

    this.createUserDto.name = this.defaultName

    await this.quickSignup(false)


  }

  async useOtherAccount() {
    await this.authenticationService.signOut();
    this.goToSlide(this.slides.signIn);
  }

  public goToSlide(slide: string) {
    this.activeSlide = slide;
  }

  openBusinessSuiteSetup = async () => {
    if (!this.completeSetupInBusinessSuiteUrl) {
      return;
    }
    await Browser.open({
      url: this.completeSetupInBusinessSuiteUrl,
      toolbarColor: '#f1f5f5',
      presentationStyle: 'popover',
    });
    if (this.closeWindowAfterSuiteContinue && typeof window !== 'undefined' && window.close) {
      window.close();
    }
  };

  goBackFromSetupIncomplete() {
    this.goToSlide(this.slides.signIn);
  }

  public quickSignup = async (emitLogin = true) => {

    ValidationUtil.validateName(this.createUserDto.name);

    await this.checkEmail();

    await this.checkPhoneNumber();

    this.createUserDto.name = this.createUserDto.name.trim()

    const isLoggedIn: boolean = await this.authenticationService.quickSignUp(this.createUserDto);

    if (emitLogin && isLoggedIn) {
      await this.login();
    }
  };

  public onEmail = async () => {
    if (
      !ValidationUtil.isEmailValid(this.createUserDto.emailVerifier.receiverId)
    ) {
      return;
    }

    await this.authenticationService.signInAnonymouslyIfNoUser();

    const user = await this.userService.checkEmail(
      this.createUserDto.emailVerifier.receiverId
    );

    if (!user) {
      this.activeSlide = this.slides.signUp;
      return;
    }

    return this.sendOtpAndLogin(this.createUserDto.emailVerifier);
  };

  public sendOtpAndLogin = async (otpVerifier: OtpVerifier, ensureAnonymousSession = true) => {
    this.isSubmittingSignIn = true;

    const res = await this.authenticationService.signInWithOtp(
      otpVerifier.receiverId,
      otpVerifier.channel,
      ensureAnonymousSession
    )

    if (res) {
      return await this.login();
    }

    this.isSubmittingSignIn = false;

  };

  public async login() {
    return this.emitLogin();
  }


  public onContinueAsLoggedInUserClicked = async () => {
    return this.emitLogin();
  }

  emitLogin = async () => {
    const user = await this.resolveAuthenticatedUser();
    if (user && this.hasMissingRequiredContactInfo(user)) {
      this.prefillRequiredContactInputs(user);
      this.goToSlide(this.slides.requiredContact);
      return;
    }
    return this.onLogin.emit();
  }

  public onContinueWithPhone = async () => {

    ValidationUtil.validatePhone(this.createUserDto.phoneVerifier.receiverId)

    await this.authenticationService.signInAnonymouslyIfNoUser();

    const user = await this.userService.isPhoneRegistered(this.createUserDto.phoneVerifier.receiverId)

    if (!user) {
      if (this.completeSetupInBusinessSuiteUrl) {
        return this.goToSlide(this.slides.setupIncomplete);
      }
      return this.signUp(this.createUserDto.phoneVerifier)
    }

    this.isSubmittingSignIn = true;
    const logInSuccess = await this.authenticationService.signInWithOtp(this.createUserDto.phoneVerifier.receiverId, OtpChannel.phone)

    if (logInSuccess) {
      return await this.login();
    }

    this.isSubmittingSignIn = false;

  };

  public onContinueWithEmail = async () => {

    const emailInput = this.createUserDto.emailVerifier.receiverId;

    ValidationUtil.validateEmail(emailInput)

    await this.authenticationService.signInAnonymouslyIfNoUser();

    const user = await this.userService.checkEmail(emailInput)

    if (!user) {
      if (this.completeSetupInBusinessSuiteUrl) {
        return this.goToSlide(this.slides.setupIncomplete);
      }
      return this.signUp(this.createUserDto.emailVerifier)
    }
    this.isSubmittingSignIn = true;
    const logInSuccess = await this.authenticationService.signInWithOtp(emailInput, OtpChannel.email)

    if (logInSuccess) {
      return await this.login();
    }

    this.isSubmittingSignIn = false;

  };

  public onSendCode = async () => {
    const otpVerifier = this.getLockedFlowOtpVerifier();

    if (!otpVerifier?.receiverId) {
      return this.goToSignUpWithDefaultName();
    }

    if (otpVerifier.channel === OtpChannel.email) {
      return this.onContinueWithEmail();
    }
    return this.onContinueWithPhone();
  };

  signUp = async (otpVerifier: OtpVerifier) => {

    if (this.isOtpVerification) {
      const otpRes = await this.otpService.sendAndVerify(otpVerifier)
      if (!otpRes?.isVerified) {
        return;
      }
    }

    return this.goToSlide(this.slides.signUp)
  }


  // Helper functions

  async checkEmail() {

    const emailInput = this.createUserDto.emailVerifier.receiverId;

    if (this.isEmailRequired && ValidationUtil.isStringEmpty(emailInput)) {
      throw new Error("Enter an Email Address")
    }

    if (ValidationUtil.isStringEmpty(emailInput)) {
      return
    }

    if (!ValidationUtil.isEmailValid(emailInput)) {
      throw new Error("Enter a Valid Email Address")
    }

    const emailUser = await this.userService.checkEmail(emailInput);

    if (emailUser) {
      this.existingAccountFoundAgainst = "email"
      this.existingAccount = emailUser;
      this.goToSlide(this.slides.accountFound)
      throw new Error("Email already exists")
    }

  }

  async checkPhoneNumber() {

    const phoneInput = this.createUserDto.phoneVerifier.receiverId;

    if (this.isPhoneRequired && ValidationUtil.isStringEmpty(phoneInput)) {
      throw new Error("Enter a Phone Number")
    }

    if (ValidationUtil.isStringEmpty(phoneInput)) {
      return
    }

    if (!ValidationUtil.isValidPhone(phoneInput)) {
      throw new Error("Enter a Valid Phone Number")
    }

    const phoneUser = await this.userService.isPhoneRegistered(phoneInput);

    if (phoneUser) {
      this.existingAccountFoundAgainst = "phone"
      this.existingAccount = phoneUser;
      this.goToSlide(this.slides.accountFound)
      throw new Error("Phone number already exists")
    }

  }


  // UI Helper functions

  switchToContinueWithEmail() {
    this.createUserDto.phoneVerifier = new OtpVerifier(OtpChannel.phone);
    if (this.lockContacts && this.defaultPhone) {
      this.createUserDto.phoneVerifier.receiverId = this.defaultPhone;
    }
    this.isLoginWithEmail = true;
  }

  switchToContinueWithPhone() {
    this.createUserDto.emailVerifier = new OtpVerifier(OtpChannel.email);
    if (this.lockContacts && this.defaultEmail) {
      this.createUserDto.emailVerifier.receiverId = this.defaultEmail;
    }
    this.isLoginWithEmail = false;
  }

  onExistingEmailOtpClicked = async () => {

    if (this.createUserDto.emailVerifier.receiverId && this.existingAccountFoundAgainst == "email") {
      return this.onContinueWithEmail()
    }
    this.goToSignInSlide()
  }

  onExistingPhoneOtpClicked = async () => {
    if (this.createUserDto.phoneVerifier.receiverId && this.existingAccountFoundAgainst == "phone") {
      return this.onContinueWithPhone()
    }
    this.goToSignInSlide()
  }

  goToSignInSlide() {
    this.createUserDto.phoneVerifier = new OtpVerifier(OtpChannel.phone);
    this.createUserDto.emailVerifier = new OtpVerifier(OtpChannel.email);
    this.createUserDto.name = null;
    this.applyLockedContactToVerifiers();
    this.isLoginWithEmail =
      !!this.defaultEmail || (!this.defaultPhone && !!this.isEmailRequired);
    this.goToSlide(this.slides.signIn);
  }

  private isLockedCollectionFlow(): boolean {
    return !!this.lockContacts;
  }

  private getLockedFlowOtpVerifier(): OtpVerifier | null {
    if (this.isLoginWithEmail && this.createUserDto.emailVerifier.receiverId) {
      return this.createUserDto.emailVerifier;
    }

    if (!this.isLoginWithEmail && this.createUserDto.phoneVerifier.receiverId) {
      return this.createUserDto.phoneVerifier;
    }

    if (this.createUserDto.emailVerifier.receiverId) {
      return this.createUserDto.emailVerifier;
    }

    if (this.createUserDto.phoneVerifier.receiverId) {
      return this.createUserDto.phoneVerifier;
    }

    return null;
  }

  private setLockedFlowContactLabel(): void {
    const otpVerifier = this.getLockedFlowOtpVerifier();
    this.lockedFlowContactLabel = otpVerifier?.receiverId ?? null;
  }

  private goToSignUpWithDefaultName(): void {
    if (this.defaultName) {
      this.createUserDto.name = this.defaultName;
    }
    this.goToSlide(this.slides.signUp);
  }

  private async prepareLockedFlowEntry(): Promise<void> {
    this.applyLockedContactToVerifiers();
    this.setLockedFlowContactLabel();

    const otpVerifier = this.getLockedFlowOtpVerifier();
    if (!otpVerifier?.receiverId) {
      return this.goToSignUpWithDefaultName();
    }

    await this.authenticationService.signInAnonymouslyIfNoUser();

    const userExists =
      otpVerifier.channel === OtpChannel.email
        ? await this.userService.checkEmail(otpVerifier.receiverId)
        : await this.userService.isPhoneRegistered(otpVerifier.receiverId);

    if (userExists) {
      return this.goToSlide(this.slides.signIn);
    }

    if (this.isOtpVerification) {
      return this.goToSlide(this.slides.signIn);
    }

    await this.authenticationService.signInAnonymouslyIfNoUser();
    return this.goToSignUpWithDefaultName();
  }

  /** Always show the logged-in / "continue as" screen first; required contact runs after Continue. */
  private routePostAuth(): void {
    this.goToSlide(this.slides.loggedIn);
  }

  private waitForAuthenticatedUser(maxWaitMs = 20000): Promise<User | null> {
    const userPromise = firstValueFrom(
      this.authenticatedUser$.pipe(
        filter((u): u is User => !!u),
        take(1)
      )
    );
    const timeoutPromise = new Promise<null>((resolve) =>
      setTimeout(() => resolve(null), maxWaitMs)
    );
    return Promise.race([userPromise, timeoutPromise]);
  }

  private hasMissingRequiredContactInfo(user: User): boolean {
    if (this.isEmailRequired && ValidationUtil.isStringEmpty(user?.email)) {
      return true;
    }
    if (this.isPhoneRequired && ValidationUtil.isStringEmpty(user?.phoneNumber)) {
      return true;
    }
    return false;
  }

  private prefillRequiredContactInputs(user: User) {
    this.createUserDto.emailVerifier.receiverId =
      user?.email || this.createUserDto.emailVerifier.receiverId || null;
    this.createUserDto.phoneVerifier.receiverId =
      user?.phoneNumber || this.createUserDto.phoneVerifier.receiverId || null;
  }

  private async refreshAuthenticatedUserFromStore(): Promise<User | null> {
    return await firstValueFrom(
      this.authenticatedUser$.pipe(
        filter((u): u is User => !!u),
        take(1)
      )
    );
  }

  private async resolveAuthenticatedUser(): Promise<User | null> {
    const fromStore = await this.waitForAuthenticatedUser();
    if (fromStore) {
      return fromStore;
    }

    const fromApi = await this.userService.getUserByToken();
    if (fromApi?._id) {
      this.store.dispatch(
        UsersActions.fetchAuthenticatedUserSuccess({ authenticatedUser: fromApi })
      );
      return fromApi;
    }

    return null;
  }

  /**
   * Locked phone values may come without country prefix.
   * For gateway locked auth we default these to India (+91) so the phone field
   * keeps `+91` and fills only the national number input.
   */
  private normalizeLockedPhone(phone: string | null | undefined): string | null {
    if (!phone) {
      return null;
    }
    const digits = phone.toString().replace(/\D/g, '');
    if (!digits) {
      return null;
    }
    if (digits.startsWith('91') && digits.length >= 12) {
      return digits;
    }
    if (digits.length <= 10) {
      return `91${digits}`;
    }
    return digits;
  }

  private normalizeComparablePhone(phone: string | null | undefined): string {
    if (!phone) {
      return '';
    }
    const normalized = this.normalizeLockedPhone(phone) ?? '';
    const digits = normalized.replace(/\D/g, '');
    return digits.length > 10 ? digits.slice(-10) : digits;
  }

  public completeRequiredContactAndContinue = async () => {
    let user = await this.resolveAuthenticatedUser();
    if (!user) {
      throw new Error("User not found");
    }

    if (this.isEmailRequired && ValidationUtil.isStringEmpty(user.email)) {
      ValidationUtil.validateEmail(this.createUserDto.emailVerifier.receiverId);
      const emailUser = await this.userService.checkEmail(this.createUserDto.emailVerifier.receiverId);
      if (emailUser?._id && emailUser._id !== user._id) {
        throw new Error("Email already associated with another account");
      }

      await this.otpService.sendAndVerify(this.createUserDto.emailVerifier, async (otpVerifier, otpInput) => {
        otpVerifier.otp = otpInput;
        const updateResponse = await this.userService.updateUserEmail(otpVerifier);
        if (updateResponse.errorMessage) {
          throw new Error(updateResponse.errorMessage);
        }
        if (updateResponse.data) {
          this.store.dispatch(
            UsersActions.fetchAuthenticatedUserSuccess({ authenticatedUser: updateResponse.data })
          );
          return true;
        }
      });
      user = (await this.refreshAuthenticatedUserFromStore()) ?? user;
    }

    if (this.isPhoneRequired && ValidationUtil.isStringEmpty(user.phoneNumber)) {
      ValidationUtil.validatePhone(this.createUserDto.phoneVerifier.receiverId);
      const phoneUser = await this.userService.isPhoneRegistered(this.createUserDto.phoneVerifier.receiverId);
      if (phoneUser?.isPhone) {
        throw new Error("Phone number already exists");
      }

      const verifyRes = await this.otpService.sendAndVerify(this.createUserDto.phoneVerifier);
      if (!verifyRes?.otpId) {
        return;
      }
      await this.userService.updateUserPhone(this.createUserDto.phoneVerifier);
    }

    const latest = await this.refreshAuthenticatedUserFromStore();
    if (latest && this.hasMissingRequiredContactInfo(latest)) {
      throw new Error("Please complete required contact details");
    }

    return this.onLogin.emit();
  };
}
